import { CalendarPlus, Church, Contact, HeartHandshake, UserRoundX, Users, UsersRound, VenetianMask } from "lucide-react"

import {
  FinanceReportCategory,
  ReportExportKind,
  ReportModule,
  type FinanceReportEntry,
} from "@/features/reports/model/report-registry-types"

/** These reports are new to the PRODUCT, not just to the catalog — nobody could open them before. */
const NEW_UNTIL = "2026-10-31"

/**
 * Reports about PEOPLE and what they take part in.
 *
 * Every one of these was already backed by the reporting engine — `members-by-status`, `group-membership`
 * and the rest have existed in `reporting-templates.catalog.ts` all along. Nothing surfaced them, because
 * the only catalog in the product was mounted inside Finance and curated the finance slice. That is the
 * whole of what was missing: curation, not capability.
 *
 * Title and description come from the engine (`reporting.templates.<id>.*`), which already has them in
 * en/fr/rw. This file is curation only — module, category, icon and the words someone might search for.
 */
function peopleEntry(
  id: string,
  module: ReportModule,
  category: FinanceReportCategory,
  icon: typeof Users,
  keywords: string[],
): FinanceReportEntry {
  return {
    id,
    module,
    category,
    icon,
    titleKey: `reporting.templates.${id}.title`,
    descriptionKey: `reporting.templates.${id}.description`,
    keywords,
    dataSource: { kind: "engine-template", templateId: id },
    // Reachable for the first time with this release; the badge retires itself on this date.
    newUntil: NEW_UNTIL,
    viewer: { layout: "result-panel", defaultView: "table" },
    export: { kind: ReportExportKind.Engine },
  }
}

export const MEMBER_ENTRIES: readonly FinanceReportEntry[] = [
  // Who the church has, and in what state.
  peopleEntry("members-by-status", ReportModule.Members, FinanceReportCategory.Membership, Contact, [
    "member", "status", "active", "visitor", "register",
  ]),
  peopleEntry("new-members-by-month", ReportModule.Members, FinanceReportCategory.Membership, CalendarPlus, [
    "new", "growth", "joined", "month", "trend",
  ]),
  peopleEntry("members-by-gender", ReportModule.Members, FinanceReportCategory.Membership, VenetianMask, [
    "gender", "men", "women", "split",
  ]),

  // Where they belong.
  peopleEntry("members-by-group", ReportModule.Members, FinanceReportCategory.Directory, UsersRound, [
    "group", "belonging", "membership",
  ]),
  peopleEntry("members-by-ministry", ReportModule.Members, FinanceReportCategory.Directory, Church, [
    "ministry", "serving", "membership",
  ]),
  peopleEntry("group-membership", ReportModule.Members, FinanceReportCategory.Directory, Users, [
    "group", "roster", "list",
  ]),
  peopleEntry("ministry-membership", ReportModule.Members, FinanceReportCategory.Directory, HeartHandshake, [
    "ministry", "roster", "list",
  ]),

  /*
   * Members in NO group — the one report here that names an absence rather than a total.
   *
   * Kept beside the belonging reports on purpose: it is the question a pastor asks straight after reading
   * them, and separating it would hide it behind a search.
   */
  peopleEntry("members-without-group", ReportModule.Members, FinanceReportCategory.Directory, UserRoundX, [
    "unassigned", "no group", "follow up", "pastoral",
  ]),
]
